'use client'

import { useEffect, useMemo, useRef, type ReactNode } from 'react'
import { AnimatePresence } from 'motion/react'

import DealHero from '@/components/content/deals/deal/DealHero'
import DealSidebar from '@/components/content/deals/deal/DealSidebar'
import DealFooter from '@/components/content/deals/deal/DealFooter'
import Meta from '@/components/structure/Meta'
import Container from '@/components/structure/Container'
import TabToggle from '@/components/navigation/Toggles'
import Section from '@/components/content/common/Section'
import RichText from '@/components/content/common/RichText'
import Tree from '@/components/content/sidebar/Tree'
import Timestamp from '@/components/ui/Timestamp'
import Authors from '@/components/content/common/Authors'
import DealsTrending from '@/components/content/deals/root/DealsTrending'
import Portal from '@/components/ui/Portal'
import DealTracker from '@/components/content/deals/deal/DealTracker'
import AuthModal from '@/components/authentication/AuthModal'
import DealDesktop from '@/components/content/deals/deal/DealDesktop'
import Sheet from '@/components/ui/Sheet'
import PreferredSource from '@/components/ui/PreferredSource'

import { Icon } from '@/icons/Icon'
import { formatEligibility } from '@/components/content/deals/helpers'
import { generateMenu } from '@/helpers/menus'
import { processAffiliateLink } from '@/lib/formatting'
import { formatNumber, isPopulatedObject, toSlug } from '@/lib/utils'
import { usePageContext } from '@/components/content/common/PageContext'
import { useDealContext } from '@/context/DealContext'
import { generateStructure, renderContent } from '@/lib/content'
import type { Block, SectionItem } from '@/types/app'
import type { BylineAuthor } from '@/lib/fetch-authors'
import type { App, Deal, DealInfo, Link, Page } from '@/payload-types'

type DealContainerProps = {
  app: App
  deals: Deal[]
  dealInfo: DealInfo | null
  catchInfo?: Page | null
  authors?: BylineAuthor[]
}

export default function DealContainer({
  app,
  deals,
  dealInfo,
  catchInfo,
  authors,
}: DealContainerProps) {
  const { dealFocus, setDealFocus } = usePageContext()
  const {
    setDealSelectionComplete,
    setAppName,
    dealTracked,
    setDealTracked,
    signIn,
    setSignIn,
    handleClaim,
    setDealId,
    setClaimValueCents,
    setEmailIsRequired,
    setDesktopIsRequired,
    showDesktopSheet,
    setShowDesktopSheet,
    setClaimed,
  } = useDealContext()
  const selectedDeal = dealFocus ?? deals[0]

  // Page-level dates come from deal_info (the canonical page record), NOT the
  // selected deal — so they stay stable as the user toggles between deals and
  // match the SEO JSON-LD exactly. modified = lastIsrDate (advances whenever any
  // of the app's deals are rebuilt); published = createdAt (first published).
  const fallbackDealDate = deals[0]?.createdAt
  const pagePublished = dealInfo?.createdAt ?? fallbackDealDate
  const pageModified =
    dealInfo?.lastIsrDate ?? dealInfo?.updatedAt ?? dealInfo?.createdAt ?? fallbackDealDate

  // catchInfo is a constant prop — derive once, not on every tab toggle.
  const catchSection = useMemo<SectionItem[]>(() => {
    const content = catchInfo?.layout?.find((block) => block.blockType === 'textTemplate')
    const blocks = content?.body?.root?.children as Block[] | undefined
    const { roots, preamble } = blocks?.length
      ? generateStructure({ content: blocks })
      : { roots: [], preamble: [] as Block[] }
    if (roots.length > 0) {
      return renderContent({ roots, preamble, collapsed: true }).sections
    }
    if (blocks?.length) {
      return [{ title: "What's the Catch?", content: <RichText content={blocks} /> }]
    }
    return []
  }, [catchInfo])

  useEffect(() => {
    const url = new URL(window.location.href)
    const urlID = url.searchParams.get('id')
    const selectedID = urlID ?? localStorage.getItem('efficient:deal_id')
    const claimId = localStorage.getItem('efficient:claim')
    const claimFromUrl = url.searchParams.get('claim') === 'true'

    let resolvedDeal = deals[0]

    if (selectedID) {
      const foundDeal = deals.find((deal) => deal.id === +selectedID)
      resolvedDeal = foundDeal ?? deals[0]
    }
    setDealFocus(resolvedDeal)

    const isClaiming = claimId === 'true' || claimFromUrl
    const emailRequired = !!resolvedDeal.emailIsRequired
    const desktopIsRequired = !!resolvedDeal.desktopIsRequired

    // Skip the cleanup when this load is also kicking off an emailRequired
    // claim: that flow re-persists deal_id below so it survives the sign-in
    // redirect round-trip (the post-auth `?claim=true` return has no `id`
    // param, so deal_id in localStorage is the only way to re-resolve the
    // right deal). Otherwise this timeout would fire ~1s later and wipe out
    // the value just re-set below.
    if (selectedID && !(isClaiming && emailRequired)) {
      setTimeout(() => {
        localStorage.removeItem('efficient:deal_id')
      }, 1000)
    }

    if (isClaiming) {
      if (emailRequired) {
        localStorage.setItem('efficient:deal_id', resolvedDeal.id.toString())
      }

      // Pass the resolved deal/app metadata directly — this runs in the same
      // effect that just resolved `resolvedDeal`, ahead of DealClaim's own
      // sync effect committing it to context, so reading dealId/appName/
      // claimValueCents from context here could submit/track stale values.
      handleClaim(false, emailRequired, desktopIsRequired, {
        dealId: resolvedDeal.id,
        appName: app.name,
        claimValueCents: resolvedDeal.claimValue ?? null,
      }).then((completed) => {
        // Only clear deal_id once the claim actually completes — if it
        // instead triggered sign-in, deal_id must survive for the post-auth
        // ?claim=true return to re-resolve this same deal.
        if (completed && emailRequired) {
          localStorage.removeItem('efficient:deal_id')
        }
      })

      setTimeout(() => {
        localStorage.removeItem('efficient:claim')
      }, 1000)
    }

    setAppName(app.name)
    setDealSelectionComplete(true)

    // Clean URL
    if (url.searchParams.has('id') || url.searchParams.has('claim')) {
      url.searchParams.delete('id')
      url.searchParams.delete('claim')
      window.history.replaceState({}, '', url.toString())
    }
  }, [deals, app.name, setDealFocus, setDealSelectionComplete, setAppName])

  // Single source of truth for syncing the selected deal into DealContext.
  // Previously each DealClaim instance (Hero + Footer both render one for the
  // same selectedDeal) ran this sync independently, writing identical values
  // twice and each tracking its own "did the id change" ref to reset `claimed`.
  //
  // previousDealIdRef guards setClaimed(false) so it only fires when the
  // selected deal actually changes (e.g. toggling between deals), not on
  // initial mount — otherwise this effect races the ?claim=true auto-claim
  // effect above and immediately resets the `claimed` state it just set.
  const previousDealIdRef = useRef<number | null>(null)
  useEffect(() => {
    setDealId(selectedDeal.id)
    setClaimValueCents(selectedDeal.claimValue ?? null)
    setEmailIsRequired(!!selectedDeal.emailIsRequired)
    setDesktopIsRequired(!!selectedDeal.desktopIsRequired)

    if (dealFocus === null) {
      return
    }

    if (previousDealIdRef.current !== null && previousDealIdRef.current !== selectedDeal.id) {
      setClaimed(false)
    }
    previousDealIdRef.current = selectedDeal.id
  }, [
    dealFocus,
    selectedDeal.id,
    selectedDeal.claimValue,
    selectedDeal.emailIsRequired,
    selectedDeal.desktopIsRequired,
    setDealId,
    setClaimValueCents,
    setEmailIsRequired,
    setDesktopIsRequired,
    setClaimed,
  ])

  const appAffiliateLink = isPopulatedObject<Link>(app.affiliateLink) ? app.affiliateLink : null
  const dealLinkSource = isPopulatedObject<Link>(selectedDeal.link)
    ? selectedDeal.link
    : appAffiliateLink

  const dealAffiliate = processAffiliateLink(
    dealLinkSource,
    app.slug,
    app.name,
    app.partnershipTier,
  )
  const dealLink = dealAffiliate.url
  const showToggle = deals.length > 1
  const dealEligibility = formatEligibility(selectedDeal.eligibility)

  const menu = generateMenu('deal', {
    app,
    deals,
    selectedDeal: selectedDeal,
    setSelectedDeal: setDealFocus,
  })
  const dealsMenu = menu.find((item) => item.label === 'Deals')?.sub ?? []

  // Overview body depends only on dealInfo (stable across tab toggles).
  // Title depends on app.name + selectedDeal.unitType (changes per tab).
  const overviewBody = useMemo(
    () =>
      dealInfo?.tldr?.root?.children ? (
        <RichText content={dealInfo.tldr.root.children as Block[]} />
      ) : (
        <>
          <p>We worked with {app.name} to get you an exclusive deal on their software.</p>
          <p>Use the link above to claim the deal.</p>
        </>
      ),
    [dealInfo, app.name],
  )

  const overviewSection: SectionItem = {
    title: `${app.name} ${selectedDeal.unitType === 'percent' || selectedDeal.unitType === 'currency' ? 'Discount' : 'Deal'}`,
    content: overviewBody,
  }

  // Terms changes per tab.
  const termsSection: SectionItem = {
    title: 'Deal Terms',
    content: <p>{selectedDeal.terms || dealEligibility.terms}</p>,
  }

  // FAQ depends only on dealInfo and showToggle; FAQ content is per-app, not
  // per-deal-tab, so selectedDeal doesn't affect the rendered children.
  const faqSection = useMemo<SectionItem[]>(() => {
    if (!dealInfo?.faqs?.length) return []
    return [
      {
        title: 'Discount & Pricing FAQs',
        content: (
          <div className="flex flex-col gap-4">
            {dealInfo.faqs.map((section, sectionIndex) => {
              const slug = toSlug(section.question ?? 'Untitled')
              return (
                <Section
                  key={`faq-${slug}-${sectionIndex}`}
                  sectionId={`discount-pricing-faqs-${slug}`}
                  title={section.question ?? 'Untitled'}
                  icon={<Icon name="circle-question" />}
                  isChild={true}
                  offset={showToggle ? 154 : 95}
                  collapsed={sectionIndex !== 0}
                  renderCollapsedContent={true}
                >
                  {section.answer?.root?.children ? (
                    <RichText content={section.answer.root.children as Block[]} />
                  ) : (
                    <p>No answer available</p>
                  )}
                </Section>
              )
            })}
          </div>
        ),
      },
    ]
  }, [dealInfo, showToggle])

  // Additional details depend only on dealInfo — stable across tab toggles.
  const additionalDetailsSection = useMemo<SectionItem>(
    () => ({
      title: 'Additional Details',
      content: dealInfo?.details?.root?.children ? (
        <RichText content={dealInfo.details.root.children as Block[]} />
      ) : (
        <p>No additional details available</p>
      ),
    }),
    [dealInfo],
  )

  const content: SectionItem[] = [
    overviewSection,
    termsSection,
    additionalDetailsSection,
    ...faqSection,
    ...catchSection,
  ]

  return (
    <>
      <Meta
        modifiedTimestamp={pageModified}
        publishedTimestamp={pagePublished}
        parent={{ label: app.name, url: `/apps/${app.slug}` }}
        stats={[
          {
            value: `${selectedDeal.claimed}`,
            label: 'deals claimed',
            shortLabel: 'claimed',
            ignoreInReview: true,
          },
          ...(selectedDeal.value !== null && selectedDeal.value !== undefined
            ? [
                {
                  value: `$${formatNumber(selectedDeal.value)}`,
                  label: 'USD saved per deal',
                },
              ]
            : []),
        ]}
      />
      <DealHero
        app={app}
        deal={selectedDeal}
        deals={deals}
        link={dealLink}
        passReferrerUrl={dealAffiliate.passReferrerUrl}
        sponsored={dealAffiliate.isSponsored}
        multiple={showToggle}
        menu={menu}
        subMenu={dealsMenu}
        timestamps={{
          modified: pageModified,
          published: pagePublished,
        }}
      />
      <article id="content" className="overlay">
        <Container
          sidebar={
            <>
              <Tree
                roots={content}
                offset={showToggle ? 162 : 96}
                header={<DealSidebar deal={selectedDeal} link={dealLink} menu={menu} />}
                toggle={showToggle}
              />
              <PreferredSource invert={true} />
              <div className="hidden flex-wrap items-center gap-y-2.5 md:flex">
                {(pageModified || pagePublished) && (
                  <Timestamp
                    modified={pageModified}
                    published={pagePublished}
                    lead="Last updated on"
                    trail="by"
                    className="pr-1"
                  />
                )}
                <Authors authors={authors} condensed={true} eagerImage={true} />
              </div>
              <DealsTrending current={selectedDeal.id} />
            </>
          }
          mobilePadding={false}
          toggle={showToggle}
        >
          {showToggle && (
            <TabToggle
              tabs={deals
                .filter((deal) => deal.name)
                .map((deal) => ({
                  icon: <Icon name="deal" />,
                  text: deal.name!,
                  action: () => setDealFocus(deal),
                }))}
            />
          )}

          {content.map((section, index) => (
            <Section
              key={`${section.title}-${index}`}
              title={section.title || ''}
              isParent={
                section.title === 'Discount & Pricing FAQs' || (section.children?.length ?? 0) > 0
              }
              offset={showToggle ? 154 : 95}
              toggle={showToggle}
            >
              {section.content as ReactNode}
            </Section>
          ))}
        </Container>

        <DealFooter
          app={app}
          deal={selectedDeal}
          link={dealLink}
          passReferrerUrl={dealAffiliate.passReferrerUrl}
          sponsored={dealAffiliate.isSponsored}
          multiple={deals.length > 1}
          menu={dealsMenu}
        />
      </article>

      <Portal>
        <AnimatePresence>
          {dealTracked && <DealTracker deal={selectedDeal} close={() => setDealTracked(false)} />}

          {signIn && (
            <AuthModal
              close={() => setSignIn(false)}
              // Land back on this deal page post-auth; ?claim=true makes
              // DealContext auto-complete the claim (see its mount effect)
              redirectUrl={`/deals/${app.slug}?claim=true`}
              config={{
                signUp: true,
              }}
            />
          )}
        </AnimatePresence>
      </Portal>

      <Sheet
        trigger={null}
        isVisible={showDesktopSheet}
        isMenu
        close={() => setShowDesktopSheet(false)}
      >
        <DealDesktop deal={selectedDeal} />
      </Sheet>
    </>
  )
}
